/**
 * Wire helpers for review photo attachment fields (ITT-927).
 * photoIds is authoritative for membership/order; photoSummary may lag.
 */

const PHOTO_STATUSES = new Set(['processing', 'ready', 'failed']);

/**
 * @param {unknown} rawPhotoIds
 * @returns {string[]}
 */
export function parseReviewPhotoIds(rawPhotoIds) {
  if (!Array.isArray(rawPhotoIds)) {
    return [];
  }
  return rawPhotoIds.filter(
    (photoId) => typeof photoId === 'string' && photoId.trim().length > 0,
  );
}

/**
 * @param {unknown} rawRevision
 * @returns {number | undefined}
 */
export function parseReviewPhotoRevision(rawRevision) {
  if (typeof rawRevision === 'number' && Number.isFinite(rawRevision)) {
    return rawRevision;
  }
  return undefined;
}

/**
 * @param {unknown} rawSummary
 * @returns {Record<string, {
 *   status: 'processing' | 'ready' | 'failed',
 *   failureCode?: string,
 *   width?: number,
 *   height?: number,
 *   variantVersion?: number,
 * }>}
 */
export function parseReviewPhotoSummary(rawSummary) {
  if (!rawSummary || typeof rawSummary !== 'object' || Array.isArray(rawSummary)) {
    return {};
  }

  const parsed = {};
  Object.entries(rawSummary).forEach(([photoId, entry]) => {
    if (typeof photoId !== 'string' || photoId.length === 0) {
      return;
    }
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const status = entry.status;
    if (!PHOTO_STATUSES.has(status)) {
      return;
    }
    const summaryEntry = { status };
    if (typeof entry.failureCode === 'string' && entry.failureCode.length > 0) {
      summaryEntry.failureCode = entry.failureCode;
    }
    if (typeof entry.width === 'number' && Number.isFinite(entry.width)) {
      summaryEntry.width = entry.width;
    }
    if (typeof entry.height === 'number' && Number.isFinite(entry.height)) {
      summaryEntry.height = entry.height;
    }
    if (
      typeof entry.variantVersion === 'number'
      && Number.isFinite(entry.variantVersion)
    ) {
      summaryEntry.variantVersion = entry.variantVersion;
    }
    parsed[photoId] = summaryEntry;
  });
  return parsed;
}

/**
 * Resolves display slots from ordered photoIds + summary projection.
 * Missing summary entries render as processing (projection lag).
 * @param {string[]} photoIds
 * @param {Record<string, { status: string }>} photoSummary
 * @returns {Array<{
 *   photoId: string,
 *   status: 'processing' | 'ready' | 'failed',
 *   failureCode?: string,
 *   width?: number,
 *   height?: number,
 *   variantVersion?: number,
 * }>}
 */
export function buildReviewPhotoSlots(photoIds, photoSummary = {}) {
  const safeIds = parseReviewPhotoIds(photoIds);
  const safeSummary = parseReviewPhotoSummary(photoSummary);

  return safeIds.map((photoId) => {
    const summaryEntry = safeSummary[photoId];
    if (!summaryEntry) {
      return { photoId, status: 'processing' };
    }
    return {
      photoId,
      status: summaryEntry.status,
      failureCode: summaryEntry.failureCode,
      width: summaryEntry.width,
      height: summaryEntry.height,
      variantVersion: summaryEntry.variantVersion,
    };
  });
}

/**
 * Extracts photo wire fields from a Firestore review document payload.
 * @param {object} reviewData
 * @returns {{
 *   photoIds: string[],
 *   photoRevision: number | undefined,
 *   photoSummary: Record<string, object>,
 * }}
 */
export function extractReviewPhotoFields(reviewData) {
  const safeReviewData =
    reviewData && typeof reviewData === 'object' ? reviewData : {};
  return {
    photoIds: parseReviewPhotoIds(safeReviewData.photoIds),
    photoRevision: parseReviewPhotoRevision(safeReviewData.photoRevision),
    photoSummary: parseReviewPhotoSummary(safeReviewData.photoSummary),
  };
}
